import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { ArrowRight } from "lucide-react";

export default function Blog() {
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [cat, setCat] = useState("All");

    useEffect(() => {
        Promise.all([api.get("/blog"), api.get("/blog/categories")]).then(([postResponse, categoryResponse]) => {
            const relevantPosts = (postResponse.data || []).filter((post) => /event|wedding|corporate|exhibition|catering|farmhouse|mice|conference|launch|promotion|gifting|celebration|venue|production|experience/i.test(`${post.title} ${post.category} ${(post.tags || []).join(" ")}`));
            setPosts(relevantPosts);
            setCategories(["All", ...new Set(relevantPosts.map((post) => post.category).filter(Boolean))]);
        }).catch(() => { setPosts([]); setCategories(["All"]); });
    }, []);

    const filtered = cat === "All" ? posts : posts.filter(p => p.category === cat);

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-10" data-testid="blog-page">
            <p className="font-mono-ui text-[#857C72]">X-Plosiv Journal</p>
            <h1 className="font-display text-5xl md:text-6xl mt-2">Ideas into experiences.</h1>
            <p className="text-[#6B645C] mt-3 max-w-xl">Planning notes, event design, celebrations and ideas for bringing people together.</p>

            <div className="mt-10 flex flex-wrap gap-2">
                {categories.map(c => (
                    <button key={c} onClick={() => setCat(c)} className={`px-4 h-9 rounded-full text-xs font-mono-ui border transition-colors ${cat === c ? "bg-[#121214] text-white border-[#121214]" : "bg-white border-[#E7DED2] hover:border-[#1B1B1F]"}`} data-testid={`blog-cat-${c.toLowerCase()}`}>
                        {c}
                    </button>
                ))}
            </div>

            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                {filtered.map((p, i) => (
                    <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.42, delay: i * 0.04 }}>
                        <Link to={`/blog/${p.slug}`} className="group block dw-tilt" data-testid={`blog-post-card-${p.slug}`}>
                            <div className="relative aspect-[4/3] rounded-[24px] overflow-hidden border border-[#E7DED2] shadow-[0_12px_30px_rgba(18,18,20,0.10)]">
                                <img src={p.cover_image} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-white/85 backdrop-blur-md text-[10px] font-mono-ui">{p.category}</span>
                            </div>
                            <div className="mt-4 px-1">
                                <h3 className="font-display text-2xl group-hover:underline leading-tight">{p.title}</h3>
                                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.excerpt}</p>
                                <p className="text-xs text-muted-foreground mt-3">{new Date(p.created_at).toLocaleDateString()} · {p.author}</p>
                            </div>
                        </Link>
                    </motion.div>
                ))}
            </div>
            {filtered.length === 0 && <div className="text-center mt-16"><p className="font-display text-2xl text-[#173c3a]">The next story is taking shape.</p><p className="text-muted-foreground mt-2">Check back for notes on event design, production and bringing people together.</p></div>}
            <div className="h-24" />
        </div>
    );
}
